import { Badge, Dialog, DialogContent, DialogHeader, DialogTitle } from '@cibli/shared';
import { useTranslation } from 'react-i18next';

import { PrinterStatus } from '@/bridge/print.bridge';

interface PrinterStatusModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  printerStatus?: PrinterStatus;
  printerIp?: string;
}

export function PrinterStatusModal({
  open,
  onOpenChange,
  printerStatus,
  printerIp,
}: PrinterStatusModalProps) {
  const { t } = useTranslation();
  if (!printerStatus) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('PrintLab.printerStatusModal.title')}</DialogTitle>
          </DialogHeader>
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              {t('PrintLab.printerStatusModal.noInfoAvailable')}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('PrintLab.printerStatusModal.title')}</DialogTitle>
        </DialogHeader>

        {printerIp && (
          <div className="mb-4">
            <p className="text-sm text-muted-foreground">
              {t('PrintLab.printerStatusModal.printerLabel')}{' '}
              <span className="font-mono">{printerIp}</span>
            </p>
          </div>
        )}

        <div className="space-y-6">
          {/* Cartouches d'encre */}
          {printerStatus.toner && (
            <div>
              <h3 className="text-lg font-semibold mb-3">
                {t('PrintLab.printerStatusModal.tonerCartridges')}
              </h3>
              <div className="space-y-3">
                {[
                  { name: 'Noir', level: printerStatus.toner.black },
                  { name: 'Cyan', level: printerStatus.toner.cyan },
                  { name: 'Magenta', level: printerStatus.toner.magenta },
                  { name: 'Jaune', level: printerStatus.toner.yellow },
                ].map(toner => (
                  <div key={toner.name} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{toner.name}</span>
                      <div className="flex items-center gap-2">
                        {toner.level?.value !== undefined && (
                          <Badge variant="outline" className="text-xs">
                            {toner.level.value}%
                          </Badge>
                        )}
                        {toner.level?.max !== undefined && (
                          <Badge variant="secondary" className="text-xs">
                            {t('PrintLab.printerStatusModal.max')}: {toner.level.max}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tambours */}
          {printerStatus.drum && (
            <div>
              <h3 className="text-lg font-semibold mb-3">
                {t('PrintLab.printerStatusModal.drums')}
              </h3>
              <div className="space-y-3">
                {[
                  { name: 'Noir', level: printerStatus.drum.black },
                  { name: 'Cyan', level: printerStatus.drum.cyan },
                  { name: 'Magenta', level: printerStatus.drum.magenta },
                  { name: 'Jaune', level: printerStatus.drum.yellow },
                ].map(drum => (
                  <div key={drum.name} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{drum.name}</span>
                      <div className="flex items-center gap-2">
                        {drum.level?.value !== undefined && (
                          <Badge variant="outline" className="text-xs">
                            {drum.level.value} {t('PrintLab.printerStatusModal.pages')}
                          </Badge>
                        )}
                        {drum.level?.max !== undefined && (
                          <Badge variant="secondary" className="text-xs">
                            {t('PrintLab.printerStatusModal.max')}: {drum.level.max}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Informations générales */}
          {(printerStatus.name || printerStatus.description || printerStatus.serialNumber) && (
            <div>
              <h3 className="text-lg font-semibold mb-3">
                {t('PrintLab.printerStatusModal.information')}
              </h3>
              <div className="space-y-2 text-sm">
                {printerStatus.name && (
                  <p>
                    <span className="font-medium">{t('PrintLab.printerStatusModal.name')}</span>{' '}
                    {printerStatus.name}
                  </p>
                )}
                {printerStatus.description && (
                  <p>
                    <span className="font-medium">
                      {t('PrintLab.printerStatusModal.description')}
                    </span>{' '}
                    {printerStatus.description}
                  </p>
                )}
                {printerStatus.serialNumber && (
                  <p>
                    <span className="font-medium">
                      {t('PrintLab.printerStatusModal.serialNumber')}
                    </span>{' '}
                    {printerStatus.serialNumber}
                  </p>
                )}
                {printerStatus.uptime && (
                  <p>
                    <span className="font-medium">{t('PrintLab.printerStatusModal.uptime')}</span>{' '}
                    {printerStatus.uptime}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Compteurs d'impression */}
          {printerStatus.counters && (
            <div>
              <h3 className="text-lg font-semibold mb-3">
                {t('PrintLab.printerStatusModal.printCounters')}
              </h3>
              <div className="space-y-2 text-sm">
                {printerStatus.counters.total !== undefined && (
                  <p>
                    <span className="font-medium">{t('PrintLab.printerStatusModal.total')}</span>{' '}
                    {printerStatus.counters.total} {t('PrintLab.printerStatusModal.pages')}
                  </p>
                )}
                {printerStatus.counters.color !== undefined && (
                  <p>
                    <span className="font-medium">{t('PrintLab.printerStatusModal.color')}</span>{' '}
                    {printerStatus.counters.color} {t('PrintLab.printerStatusModal.pages')}
                  </p>
                )}
                {printerStatus.counters.mono !== undefined && (
                  <p>
                    <span className="font-medium">
                      {t('PrintLab.printerStatusModal.monochrome')}
                    </span>{' '}
                    {printerStatus.counters.mono} {t('PrintLab.printerStatusModal.pages')}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
